/**
 * Pure helpers tying an agency bien (`AgencyProperty`) to its mandate(s).
 *
 * A mandate is the legal authorisation to market a bien. The link is loose and
 * bidirectional (see agency-mandate.ts / agency-property.ts):
 *  - agency_created mandate → `mandate.propertyId` points at the bien;
 *  - owner_shared mandate converted into a bien → `mandate.generatedPropertyId`
 *    points at the bien produced by `createPropertyFromMandate`.
 *
 * A bien can carry SEVERAL mandates over its life (renewal, exclusive→open, an
 * expired one plus a fresh one), so coverage is derived from the whole mandate
 * set, never a single `property.mandateId` field.
 *
 * These functions are framework-free and exhaustively unit-tested so the
 * "bien sans mandat" alerting can't silently regress.
 */

import { AgencyMandate, MandateStatus } from '../../../shared/definitions/agency-mandate';
import { AgencyProperty } from '../../../shared/definitions/agency-property';

/** Coverage state of a bien with respect to its mandate(s). */
export type MandateCoverage = 'active' | 'pending' | 'none';

/**
 * Mandate statuses that no longer cover a bien — a bien linked only to these is
 * effectively "sans mandat" again (and the agency can re-send a fresh one).
 */
const TERMINAL_STATUSES: ReadonlySet<MandateStatus> = new Set<MandateStatus>([
  'expired',
  'terminated',
]);

/** True when `mandate` is linked to `propertyId` (either link direction). */
function linksProperty(mandate: AgencyMandate, propertyId: string): boolean {
  return mandate.propertyId === propertyId || mandate.generatedPropertyId === propertyId;
}

/**
 * All mandates linked to the given bien, in the order they appear in `mandates`.
 * Pass the full agency mandate list (already streamed by `mandates$()`).
 */
export function mandatesForProperty(
  propertyId: string,
  mandates: readonly AgencyMandate[],
): AgencyMandate[] {
  if (!propertyId) return [];
  return mandates.filter((m) => linksProperty(m, propertyId));
}

/** True when the mandate is a declined e-signature (no longer covering). */
function isDeclined(mandate: AgencyMandate): boolean {
  return mandate.signature?.status === 'declined';
}

/**
 * Coverage state of `property` given the full mandate list:
 *  - `active`  → at least one linked mandate is `status === 'active'` (signed/live);
 *  - `pending` → no active one, but a linked mandate is still in progress
 *                (a draft, or an e-signature sent/awaiting signature);
 *  - `none`    → no linked mandate covers it (none at all, or only
 *                declined/expired/terminated) → surfaced as "bien sans mandat".
 */
export function mandateCoverage(
  property: Pick<AgencyProperty, 'propertyId'>,
  mandates: readonly AgencyMandate[],
): MandateCoverage {
  const linked = mandatesForProperty(property.propertyId, mandates).filter(
    (m) => !isDeclined(m) && !TERMINAL_STATUSES.has(m.status),
  );
  if (linked.length === 0) return 'none';
  if (linked.some((m) => m.status === 'active')) return 'active';
  return 'pending';
}

/** Convenience: a bien with no covering mandate (drives the alert/badge). */
export function isWithoutMandate(
  property: Pick<AgencyProperty, 'propertyId'>,
  mandates: readonly AgencyMandate[],
): boolean {
  return mandateCoverage(property, mandates) === 'none';
}

/** Count of biens with no covering mandate (drives the dashboard alert). */
export function countWithoutMandate(
  properties: readonly Pick<AgencyProperty, 'propertyId'>[],
  mandates: readonly AgencyMandate[],
): number {
  return properties.reduce((n, p) => (isWithoutMandate(p, mandates) ? n + 1 : n), 0);
}
